import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { slideshowApi, getFileUrl, getEventFeedWsUrl, musicApi, spotifyApi, eventConfigApi } from "../lib/api";
import { Camera, Loader2 } from "lucide-react";
import QRCode from "react-qr-code";

export default function TVSlideshow() {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();

  // Persist any tv_token coming via URL (after Netflix-style pairing) so
  // the page can survive refresh and the request interceptor picks it up
  // automatically for authenticated endpoints (e.g. /spotify/token).
  useEffect(() => {
    const incoming = searchParams.get("tv_token");
    if (incoming && eventId) {
      try {
        localStorage.setItem(`festa_tv_token_${eventId}`, incoming);
      } catch (_) { /* noop */ }
    }
  }, [searchParams, eventId]);

  const [media, setMedia] = useState([]);
  const [event, setEvent] = useState(null);
  const [revoked, setRevoked] = useState(false);
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [videoFailed, setVideoFailed] = useState(false);
  const [playlist, setPlaylist] = useState([]);
  const [musicTrackIndex, setMusicTrackIndex] = useState(0);
  // Whether the user has clicked once to unlock audio autoplay (browsers
  // block autoplay of sound until the user interacts with the page).
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  // Spotify (per-event). When the host has connected Spotify and selected a
  // playlist, we use the Web Playback SDK to play their playlist instead of
  // the uploaded MP3 list. Falls back gracefully on errors.
  const [spotifyStatus, setSpotifyStatus] = useState(null); // {connected, is_premium, playlist_uri,...}
  const [spotifyReady, setSpotifyReady] = useState(false);
  const [spotifyError, setSpotifyError] = useState("");
  const spotifyPlayerRef = useRef(null);
  const spotifyDeviceIdRef = useRef(null);
  // Live TV (YouTube) — per-event split-screen overlay. When enabled, the
  // slideshow shrinks to ~70% of the width and a YouTube embed fills the
  // remaining 30%. `audio_mode` decides which source plays sound.
  const [liveTv, setLiveTv] = useState({ enabled: false, video_id: "", channel: "", provider: "none", audio_mode: "spotify" });
  // Auto-recovery: a key we bump to force the iframe to reload when it
  // stalls (YouTube/Twitch live streams sometimes time out after hours).
  const [liveTvReloadKey, setLiveTvReloadKey] = useState(0);
  // Whether the document is currently in fullscreen. Entering fullscreen
  // requires a user gesture (browser policy), so we offer a one-click
  // overlay. Pressing ESC exits — handled natively by the browser; we
  // just listen to fullscreenchange to keep state in sync.
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Hide the cursor after a short period of mouse inactivity so the TV
  // shows zero browser/OS chrome.
  const [cursorIdle, setCursorIdle] = useState(false);
  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const wsRef = useRef(null);
  const wsReconnectRef = useRef(null);
  const wsAttemptRef = useRef(0);
  const currentIndexRef = useRef(0);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  // Track native fullscreen state (so the entry overlay disappears once
  // we are fullscreen, and reappears if the user presses ESC).
  useEffect(() => {
    const onChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  // Hide the mouse cursor after 3s of inactivity to keep the TV totally
  // chrome-free. Any movement brings it back.
  useEffect(() => {
    let timer;
    const wake = () => {
      setCursorIdle(false);
      clearTimeout(timer);
      timer = setTimeout(() => setCursorIdle(true), 3000);
    };
    wake();
    window.addEventListener("mousemove", wake);
    window.addEventListener("mousedown", wake);
    window.addEventListener("keydown", wake);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("mousemove", wake);
      window.removeEventListener("mousedown", wake);
      window.removeEventListener("keydown", wake);
    };
  }, []);

  // Single-click entry: enter native fullscreen AND unlock audio autoplay.
  // Required because browsers block both without a user gesture.
  const enterTvMode = useCallback(async () => {
    setAudioUnlocked(true);
    try {
      const el = document.documentElement;
      const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
      if (req) await req.call(el);
    } catch (e) {
      // Fullscreen denied (e.g. iframe without allowfullscreen). Audio
      // unlock still proceeds.
      console.warn("Fullscreen denied:", e?.message || e);
    }
  }, []);

  // Load TV background music playlist for this event.
  useEffect(() => {
    if (!eventId) return;
    musicApi
      .publicList(eventId)
      .then(({ data }) => setPlaylist(data || []))
      .catch((e) => console.warn("Music playlist load failed:", e?.message || e));
  }, [eventId]);

  // Detect Spotify connection for this event. This requires the host to be
  // logged in on this browser (Bearer token in localStorage). If Spotify is
  // connected with a playlist + Premium, we'll use it as the audio source
  // and skip the MP3 player.
  useEffect(() => {
    if (!eventId) return;
    spotifyApi
      .status(eventId)
      .then(({ data }) => setSpotifyStatus(data || null))
      .catch(() => setSpotifyStatus({ connected: false }));
  }, [eventId]);

  // Detect live TV (YouTube) config for this event. Public endpoint, so it
  // works on any opened TV mode page (host not required).
  useEffect(() => {
    if (!eventId) return;
    eventConfigApi
      .getLiveTv(eventId)
      .then(({ data }) =>
        setLiveTv({
          enabled: !!data?.enabled,
          video_id: data?.video_id || "",
          channel: data?.channel || "",
          provider: data?.provider || (data?.video_id ? "youtube" : "none"),
          audio_mode: data?.audio_mode || "spotify",
        })
      )
      .catch(() => setLiveTv({ enabled: false, video_id: "", channel: "", provider: "none", audio_mode: "spotify" }));
  }, [eventId]);

  // Whether the live TV split-screen overlay is currently active and renderable.
  const liveTvActive =
    liveTv.enabled &&
    ((liveTv.provider === "youtube" && Boolean(liveTv.video_id)) ||
     (liveTv.provider === "twitch" && Boolean(liveTv.channel)));

  // Auto-recovery: periodically force-reload the live TV iframe to avoid
  // YouTube/Twitch's "Ocorreu um erro" state after several hours of embed.
  useEffect(() => {
    if (!liveTvActive) return;
    const RELOAD_EVERY_MS = 45 * 60 * 1000; // 45 minutes
    const t = setInterval(() => {
      setLiveTvReloadKey((k) => k + 1);
    }, RELOAD_EVERY_MS);
    return () => clearInterval(t);
  }, [liveTvActive]);

  // Auto-dismiss transient Spotify errors after 5s so they don't stay
  // glued to the screen if the SDK recovered (e.g. token refresh, scope
  // rejection from a stale session).
  useEffect(() => {
    if (!spotifyError) return;
    const t = setTimeout(() => setSpotifyError(""), 5000);
    return () => clearTimeout(t);
  }, [spotifyError]);

  // Compute whether Spotify should be used as the audio source. If the
  // host enabled live TV with audio_mode != "spotify", we suppress Spotify
  // playback so it doesn't fight the YouTube audio.
  const spotifyAvailable = Boolean(
    spotifyStatus?.connected &&
    spotifyStatus?.is_premium &&
    spotifyStatus?.playlist_uri
  );
  const useSpotify = spotifyAvailable && (!liveTvActive || liveTv.audio_mode === "spotify");

  // ==================== SPOTIFY WEB PLAYBACK SDK ====================
  // Initialise once we know the event uses Spotify AND the user has clicked
  // the entry overlay (audio unlock = required user gesture for SDK init).
  useEffect(() => {
    if (!useSpotify || !audioUnlocked) return;
    let cancelled = false;
    let scriptEl = null;

    const onSDKReady = () => {
      if (cancelled || !window.Spotify) return;
      const player = new window.Spotify.Player({
        name: `Festa.IA TV - ${event?.name || eventId}`,
        getOAuthToken: async (cb) => {
          try {
            const { data } = await spotifyApi.token(eventId);
            cb(data?.access_token || "");
          } catch (e) {
            console.error("Spotify token fetch failed", e);
            setSpotifyError("Falha ao obter token Spotify.");
          }
        },
        volume: 0.6,
      });
      spotifyPlayerRef.current = player;
      player.addListener("ready", async ({ device_id }) => {
        spotifyDeviceIdRef.current = device_id;
        setSpotifyReady(true);
        // Clear any earlier transient errors (e.g. stale-scope rejections
        // from the previous session) once the SDK reaches the ready state.
        setSpotifyError("");

        // Some browsers (Safari, mobile) require activateElement() to be
        // called from a user gesture before the SDK can play media. Calling
        // it again on ready is a safe no-op when not needed.
        try {
          if (typeof player.activateElement === "function") {
            await player.activateElement();
          }
        } catch (_) { /* noop */ }

        // Helper: small delay so Spotify's backend propagates the device
        // before we try to control it. Without this we frequently see
        // "404 Not Found" because the device just registered.
        const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
        const fetchSpotify = (url, opts) =>
          fetch(url, opts).then((r) => ({ status: r.status, ok: r.ok }));

        // Transfer playback to this device and start the playlist context.
        try {
          const { data } = await spotifyApi.token(eventId);
          const token = data?.access_token;

          // Step 1: transfer to our device. play:true lets Spotify resume
          // immediately if the device was already the active one.
          await fetchSpotify("https://api.spotify.com/v1/me/player", {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({ device_ids: [device_id], play: false }),
          });

          // Wait for Spotify to register the device on their side.
          await sleep(700);

          // Step 2: enable shuffle (best-effort).
          await fetchSpotify(`https://api.spotify.com/v1/me/player/shuffle?state=true&device_id=${device_id}`, {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}` },
          });

          // Step 3: play the playlist context with retries. The device
          // sometimes takes 1-2s to be ready for play after transfer,
          // returning 404 in the meantime.
          let lastStatus = 0;
          for (let attempt = 0; attempt < 5; attempt += 1) {
            if (cancelled) return;
            const { status, ok } = await fetchSpotify(
              `https://api.spotify.com/v1/me/player/play?device_id=${device_id}`,
              {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify({ context_uri: spotifyStatus.playlist_uri }),
              }
            );
            lastStatus = status;
            if (ok || status === 204) break;
            // 404 = device not yet active, 502/503 = transient, 401 = token
            // rotation; retry with growing backoff.
            await sleep(500 * (attempt + 1));
          }
          if (lastStatus && lastStatus !== 204 && lastStatus < 200) {
            setSpotifyError("Falha ao iniciar playlist (HTTP " + lastStatus + ").");
          }
        } catch (e) {
          console.error("Spotify start playback failed", e);
          setSpotifyError("Falha ao iniciar a playlist.");
        }
      });
      player.addListener("not_ready", () => {
        setSpotifyReady(false);
      });
      player.addListener("initialization_error", ({ message }) => setSpotifyError(message));
      player.addListener("authentication_error", ({ message }) => setSpotifyError(message));
      player.addListener("account_error", ({ message }) => setSpotifyError(message));
      player.connect();
    };

    if (window.Spotify) {
      onSDKReady();
    } else {
      window.onSpotifyWebPlaybackSDKReady = onSDKReady;
      scriptEl = document.createElement("script");
      scriptEl.src = "https://sdk.scdn.co/spotify-player.js";
      scriptEl.async = true;
      document.body.appendChild(scriptEl);
    }

    return () => {
      cancelled = true;
      // Best-effort: pause playback then disconnect the player. Pausing
      // before disconnect helps Spotify free the device on their side, so
      // re-entering the TV mode doesn't collide with the previous device
      // state and 404 on /me/player.
      try {
        const p = spotifyPlayerRef.current;
        if (p) {
          try { p.pause?.(); } catch (_) { /* noop */ }
          p.disconnect();
        }
      } catch (_) { /* noop */ }
      spotifyPlayerRef.current = null;
      spotifyDeviceIdRef.current = null;
      setSpotifyReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useSpotify, audioUnlocked, eventId]);

  useEffect(() => {
    loadSlideshow();
    // Kept as a safety-net refresh in case we miss a WS event (network
    // blip, server restart, etc.). Much slower now since WS drives it.
    const poll = setInterval(loadSlideshow, 60000);

    // Realtime: listen for new_media pushes and prepend so the TV can show
    // a freshly-posted photo/video on the very next slide rotation.
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      let ws;
      try {
        ws = new WebSocket(getEventFeedWsUrl(eventId));
      } catch (_) {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        wsAttemptRef.current = 0;
      };

      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch (_) { return; }
        if (!msg || typeof msg !== "object") return;
        if (msg.type === "tv:revoked") {
          // Anfitriao desconectou esta TV do dashboard. Show overlay,
          // wipe the local token, and after 5s return to the pairing page.
          try {
            const tokenKey = `festa_tv_token_${eventId}`;
            const local = localStorage.getItem(tokenKey);
            // Only react if our own token was affected. If msg has a suffix
            // and it doesn't match ours, ignore — different device.
            if (local && msg.token_suffix && !local.endsWith(msg.token_suffix)) {
              return;
            }
            localStorage.removeItem(tokenKey);
          } catch (_) { /* ignore */ }
          setRevoked(true);
          setTimeout(() => {
            try { navigate("/tv"); } catch (_) { window.location.href = "/tv"; }
          }, 5000);
          return;
        }
        if (msg.type === "new_media" && msg.media) {
          const item = msg.media;
          // Respect the guest's "do not show on TV" choice. Legacy media
          // missing this field is treated as visible.
          if (item.show_on_tv === false) return;
          // Pure updater: insert right AFTER the currently-displayed slide
          // so it becomes the very next thing on screen when goNext fires,
          // without interrupting the current slide.
          const insertAtSnapshot = currentIndexRef.current;
          setMedia((prev) => {
            if (prev.some((m) => m.media_id === item.media_id)) return prev;
            if (prev.length === 0) return [item];
            const insertAt = Math.min(insertAtSnapshot + 1, prev.length);
            return [...prev.slice(0, insertAt), item, ...prev.slice(insertAt)];
          });
        } else if (msg.type === "show_on_tv_update" && msg.media_id) {
          // Host toggled visibility on the feed; sync the slideshow live.
          // If hidden -> remove it from the rotation. If shown -> we don't
          // re-fetch here; the next loadSlideshow refresh (60s safety net)
          // will pick it up. Hiding immediately is the urgent case.
          if (msg.show_on_tv === false) {
            setMedia((prev) => prev.filter((m) => m.media_id !== msg.media_id));
          } else {
            // Trigger an immediate refresh so the unhidden item reappears
            // without waiting for the next safety-net poll.
            loadSlideshow();
          }
        } else if (msg.type === "live_tv_update") {
          setLiveTv({
            enabled: !!msg.enabled,
            video_id: msg.video_id || "",
            channel: msg.channel || "",
            provider: msg.provider || (msg.video_id ? "youtube" : "none"),
            audio_mode: msg.audio_mode || "spotify",
          });
          setLiveTvReloadKey((k) => k + 1);
        }
      };

      ws.onclose = () => {
        if (wsRef.current === ws) wsRef.current = null;
        scheduleReconnect();
      };
      ws.onerror = () => {
        try { ws.close(); } catch (_) {}
      };
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      const attempt = wsAttemptRef.current + 1;
      wsAttemptRef.current = attempt;
      const delay = Math.min(30000, 1000 * 2 ** Math.min(attempt - 1, 5));
      if (wsReconnectRef.current) clearTimeout(wsReconnectRef.current);
      wsReconnectRef.current = setTimeout(connect, delay);
    };

    connect();

    return () => {
      cancelled = true;
      clearInterval(poll);
      if (wsReconnectRef.current) clearTimeout(wsReconnectRef.current);
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) {
        try { ws.close(); } catch (_) {}
      }
    };
  }, [eventId]);

  const loadSlideshow = async () => {
    try {
      const { data } = await slideshowApi.get(eventId);
      setMedia(data.media || []);
      setEvent(data.event);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const currentItem = media[currentIndex];
  const isVideo = currentItem?.type === "video" || currentItem?.content_type?.startsWith("video");
  const joinUrl = event?.join_url || "";

  const goNext = useCallback(() => {
    if (media.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % media.length);
  }, [media.length]);

  // Background music control: pause when a video is on screen, resume on
  // photos. Works for both the local MP3 playlist AND the Spotify SDK.
  // Browsers block autoplay until user interaction, so playback only starts
  // after `audioUnlocked` becomes true. Also forces silence when live TV
  // is on with audio_mode != "spotify".
  useEffect(() => {
    const liveTvSilencesUs = liveTvActive && liveTv.audio_mode !== "spotify";
    // Spotify branch — control via Web Playback SDK
    if (useSpotify && spotifyPlayerRef.current && spotifyReady) {
      const player = spotifyPlayerRef.current;
      if (isVideo || liveTvSilencesUs) {
        player.pause().catch(() => {});
      } else {
        player.resume().catch(() => {});
      }
      return;
    }
    // Local MP3 branch
    const audio = audioRef.current;
    if (!audio || playlist.length === 0 || !audioUnlocked) return;
    if (isVideo || liveTvSilencesUs) {
      audio.pause();
    } else {
      const p = audio.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          // Autoplay still denied (rare). Mark as not unlocked yet so the
          // overlay reappears.
          setAudioUnlocked(false);
        });
      }
    }
  }, [isVideo, audioUnlocked, playlist.length, musicTrackIndex, useSpotify, spotifyReady, liveTvActive, liveTv.audio_mode]);

  const handleAudioEnded = () => {
    if (playlist.length === 0) return;
    setMusicTrackIndex((i) => (i + 1) % playlist.length);
  };

  // Reset video failure flag whenever we switch to a new item and try to play
  useEffect(() => {
    setVideoFailed(false);
    if (isVideo && videoRef.current) {
      // Kickstart playback. Muted+playsInline is required for browser
      // autoplay policies (Chrome/Safari). Once the user has clicked the
      // entry overlay (audioUnlocked=true) we can play with sound, since
      // there's a user gesture on record.
      const v = videoRef.current;
      v.muted = !audioUnlocked;
      const p = v.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          // Autoplay denied - just stay paused on the first frame.
        });
      }
    }
  }, [currentIndex, isVideo, audioUnlocked]);

  useEffect(() => {
    if (!currentItem || media.length === 0) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    // For videos we rely primarily on the `onEnded` event to advance.
    // We still set a hard timeout (15s) as a safety net in case the video
    // fails to emit `ended` (e.g., network stall, codec issue).
    const delay = isVideo ? 15000 : 10000;
    timerRef.current = setTimeout(goNext, delay);
    return () => clearTimeout(timerRef.current);
  }, [currentIndex, currentItem, isVideo, goNext, media.length]);

  if (loading) {
    return (
      <div className="h-screen w-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-[#FF6B6B]" />
      </div>
    );
  }

  return (
    <div
      className={`h-screen w-screen bg-[#0A0A0A] overflow-hidden relative ${cursorIdle ? "cursor-none" : ""}`}
      data-testid="tv-slideshow-page"
    >
      {revoked && (
        <div
          className="absolute inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-xl"
          data-testid="tv-revoked-overlay"
        >
          <div className="text-center max-w-2xl px-8">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-[#FF6B6B] border-4 border-white rounded-3xl mb-8 shadow-[8px_8px_0px_#FFFFFF]">
              <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
                <line x1="2" y1="2" x2="22" y2="22" />
              </svg>
            </div>
            <p className="text-[#FF6B6B] uppercase tracking-[0.3em] font-bold text-sm mb-4">
              Sessao encerrada
            </p>
            <h1
              className="text-5xl md:text-6xl font-black text-white mb-6 leading-tight"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Esta TV foi desconectada pelo anfitriao
            </h1>
            <p className="text-xl text-[#A8A29E]">
              Voltando para o pareamento em alguns segundos...
            </p>
          </div>
        </div>
      )}
      {/* When live TV is active, we dock a YouTube iframe on the right 50%
          of the screen and shrink the slideshow column to the left 50%. */}
      {liveTvActive && (
        <div
          className="absolute top-0 right-0 h-full bg-black border-l-2 border-[#1C1917] z-20"
          style={{ width: "50%" }}
          data-testid="tv-livetv-frame"
        >
          {liveTv.provider === "youtube" ? (
            <iframe
              key={`yt-${liveTv.video_id}-${liveTvReloadKey}`}
              title="TV ao vivo"
              src={`https://www.youtube.com/embed/${liveTv.video_id}?autoplay=1&mute=${liveTv.audio_mode === "youtube" ? 0 : 1}&rel=0&modestbranding=1&playsinline=1`}
              className="w-full h-full"
              frameBorder="0"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          ) : liveTv.provider === "twitch" ? (
            <iframe
              key={`tw-${liveTv.channel}-${liveTvReloadKey}`}
              title="TV ao vivo Twitch"
              src={`https://player.twitch.tv/?channel=${encodeURIComponent(liveTv.channel)}&parent=${window.location.hostname}&autoplay=true&muted=${liveTv.audio_mode === "youtube" ? "false" : "true"}`}
              className="w-full h-full"
              frameBorder="0"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          ) : null}
        </div>
      )}

      {/* Slideshow column wrapper. Takes 50% when live TV is on, full width
          otherwise. All existing slideshow content is rendered into this. */}
      <div
        className="absolute top-0 left-0 h-full"
        style={{ width: liveTvActive ? "50%" : "100%" }}
        data-testid="tv-slideshow-column"
      >
      {/* Background music player. Hidden audio element controlled via ref.
          Only mounted when there is a local MP3 playlist AND we're not using
          Spotify (Spotify takes precedence per-event). */}
      {!useSpotify && playlist.length > 0 && liveTv.audio_mode !== "youtube" && liveTv.audio_mode !== "muted" && (
        <audio
          ref={audioRef}
          src={getFileUrl(playlist[musicTrackIndex]?.storage_path)}
          onEnded={handleAudioEnded}
          preload="auto"
          data-testid="tv-music-audio"
        />
      )}

      {/* Spotify error toast (rare, e.g. token failure or non-Premium account
          slipped through). Non-blocking. */}
      {useSpotify && spotifyError && (
        <div className="absolute z-30 top-4 right-4 bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-full border-2 border-white" data-testid="tv-spotify-error">
          Spotify: {spotifyError}
        </div>
      )}

      {/* Entry overlay: one click enters browser fullscreen (so no browser
          chrome is visible) AND unlocks audio autoplay if a playlist is
          configured. Hidden once the document is in fullscreen. ESC exits
          fullscreen natively and brings this overlay back. */}
      {!isFullscreen && (
        <button
          onClick={enterTvMode}
          className="absolute z-40 inset-0 w-full h-full bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center gap-4 text-white"
          data-testid="tv-enter-fullscreen-btn"
        >
          <div className="text-5xl font-extrabold tracking-tight">Modo TV</div>
          <div className="px-6 py-3 bg-[#FF6B6B] border-2 border-white text-white text-sm font-bold uppercase tracking-wider rounded-full shadow-[3px_3px_0px_#000]">
            ▶ Entrar em tela cheia
            {useSpotify ? " e iniciar Spotify" : playlist.length > 0 ? " e iniciar musica" : ""}
          </div>
          <div className="text-xs text-white/70 mt-2">Pressione ESC para sair</div>
        </button>
      )}

      {/* Image: blurred fill background + sharp "contain" foreground so the
          photo is never cropped. The blurred background uses the same image
          so the empty letter/pillar-box space feels cohesive with the main
          photo (classic "ambient backdrop" look). */}
      {currentItem && !isVideo && (
        <div key={currentItem.media_id} className="absolute inset-0">
          {/* Blurred background */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${getFileUrl(currentItem.storage_path)})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "blur(40px) brightness(0.5)",
              transform: "scale(1.15)",
            }}
          />
          {/* Sharp foreground image, fully visible (letter/pillar boxed) */}
          <img
            src={getFileUrl(currentItem.storage_path)}
            alt=""
            className="absolute inset-0 w-full h-full object-contain"
            style={{ animation: "kenburnsContain 12s ease-in-out forwards" }}
            data-testid="tv-image"
          />
        </div>
      )}

      {/* Video: try MP4 first (autoplay muted+playsInline works in browsers),
          fallback to GIF preview if autoplay is denied or video errors. */}
      {currentItem && isVideo && !videoFailed && (
        <div key={currentItem.media_id} className="absolute inset-0">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${getFileUrl(currentItem.gif_path || currentItem.storage_path)})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "blur(40px) brightness(0.5)",
              transform: "scale(1.15)",
            }}
          />
          <video
            ref={videoRef}
            src={getFileUrl(currentItem.storage_path)}
            className="absolute inset-0 w-full h-full object-contain"
            autoPlay
            muted={!audioUnlocked}
            playsInline
            loop={false}
            preload="auto"
            onEnded={goNext}
            onError={() => setVideoFailed(true)}
            data-testid="tv-video-mp4"
          />
        </div>
      )}

      {/* Fallback: if autoplay was blocked or MP4 failed, show GIF preview */}
      {currentItem && isVideo && videoFailed && (
        <div key={`${currentItem.media_id}-gif`} className="absolute inset-0">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${getFileUrl(currentItem.gif_path || currentItem.storage_path)})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "blur(40px) brightness(0.5)",
              transform: "scale(1.15)",
            }}
          />
          <img
            src={getFileUrl(currentItem.gif_path || currentItem.storage_path)}
            alt=""
            className="absolute inset-0 w-full h-full object-contain"
            data-testid="tv-video-gif"
          />
        </div>
      )}

      {/* Overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40" />

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 p-6 flex items-center justify-between z-10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#FF6B6B] rounded-lg flex items-center justify-center">
            <Camera className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="text-white font-black text-2xl tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {event?.name || "Festa.IA"}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2 text-white/60 text-sm">
          <div className="w-2 h-2 bg-[#FF6B6B] rounded-full animate-pulse" />
          AO VIVO
        </div>
      </div>

      {/* Photo info */}
      {currentItem && (
        <div className="absolute bottom-24 left-6 right-6 z-10">
          <p className="text-white/80 text-sm font-bold uppercase tracking-wider mb-1">
            {currentItem.nickname}
          </p>
          {/* AI-generated description and category chips intentionally
              hidden on TV mode: the host wants the slideshow to stay
              visually clean. The data is still available on the guest feed. */}
        </div>
      )}

      {/* QR Code */}
      {joinUrl && (
        <div className="absolute bottom-6 right-6 z-10 bg-white p-3 rounded-xl shadow-lg" data-testid="tv-qr-code">
          <QRCode value={joinUrl} size={80} />
          <p className="text-[8px] text-center text-[#78716C] mt-1 font-bold">ENTRAR</p>
        </div>
      )}

      {/* Progress dots */}
      <div className="absolute bottom-6 left-6 flex gap-1 z-10">
        {media.slice(0, 10).map((_, idx) => (
          <div
            key={idx}
            className={`w-2 h-2 rounded-full transition-all ${idx === currentIndex % 10 ? "bg-[#FF6B6B] w-6" : "bg-white/40"}`}
          />
        ))}
      </div>

      {/* No media state */}
      {media.length === 0 && (
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
          <Camera className="w-16 h-16 text-white/40 mb-4" strokeWidth={2.5} />
          <h2 className="text-white text-2xl font-black" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Aguardando midia...
          </h2>
          <p className="text-white/60 mt-2">Escaneie o QR Code para enviar midias</p>
          {joinUrl && (
            <div className="mt-8 bg-white p-4 rounded-xl">
              <QRCode value={joinUrl} size={160} />
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes kenburns {
          0% { transform: scale(1) translate(0, 0); }
          100% { transform: scale(1.08) translate(-1%, -1%); }
        }
        @keyframes kenburnsContain {
          0% { transform: scale(1); }
          100% { transform: scale(1.04); }
        }
      `}</style>
      </div>
    </div>
  );
}
